import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import productData, { siteConfig } from "../Datas/productData";
import { CartContext } from "./CartContext";
import "../css/productdetails.scss";

const formatMoney = (value) =>
  new Intl.NumberFormat(siteConfig.currency.locale, {
    style: "currency",
    currency: siteConfig.currency.code,
  }).format(Number(value) || 0);

const ProductDetail = () => {
  const { id } = useParams();
  const product = productData.find((item) => item.id === Number(id));
  const { addToCart, toggleWishlist, isInWishlist } =
    useContext(CartContext);

  const galleryImages = useMemo(() => {
    if (!product) return [];
    if (Array.isArray(product.images) && product.images.length) {
      return product.images;
    }
    return product.image ? [product.image] : [];
  }, [product]);

  const [selectedSize, setSelectedSize] = useState(product?.size?.[0] || "");
  const [selectedColor, setSelectedColor] = useState(
    product?.colors?.[0] || ""
  );
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [feedback, setFeedback] = useState("");
  const [failedImages, setFailedImages] = useState({});

  useEffect(() => {
    setSelectedSize(product?.size?.[0] || "");
    setSelectedColor(product?.colors?.[0] || "");
    setSelectedImage(0);
    setQuantity(1);
    setFeedback("");
    setFailedImages({});
  }, [product]);

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!product) {
    return (
      <main className="product-detail product-detail--missing">
        <p className="product-detail__eyebrow">404 / Collection</p>
        <h1>This piece could not be found.</h1>
        <p>It may have moved or is no longer part of the current collection.</p>
        <Link className="product-detail__back" onClick={toTop} to="/shop">
          Explore the collection
        </Link>
      </main>
    );
  }

  const sizeOptions = product.size?.length ? product.size : ["One size"];
  const colorOptions = product.colors?.length ? product.colors : ["Standard"];
  const stock = Math.max(0, Number(product.stock ?? 10));
  const soldOut = stock === 0;
  const mainImage = galleryImages[selectedImage];
  const wishlisted =
    typeof isInWishlist === "function" ? isInWishlist(product.id) : false;

  const changeQuantity = (nextQuantity) => {
    setQuantity(Math.min(Math.max(1, nextQuantity), Math.max(1, stock)));
  };

  const handleAddToCart = () => {
    if (soldOut) return;
    addToCart(product.id, quantity, selectedSize, selectedColor);
    setFeedback(
      `${quantity} ${quantity === 1 ? "piece" : "pieces"} added to your bag.`
    );
  };

  const handleWishlist = () => {
    if (typeof toggleWishlist === "function") {
      toggleWishlist(product.id);
    }
  };

  const markImageAsFailed = (source) => {
    setFailedImages((current) => ({ ...current, [source]: true }));
  };

  const attributes = Object.entries(product.attributes || {});
  const features = product.Features || [];

  return (
    <main className="product-detail">
      <nav className="product-detail__breadcrumb" aria-label="Breadcrumb">
        <Link to="/shop" onClick={toTop}>
          Shop
        </Link>
        <span aria-hidden="true">/</span>
        <span>{product.category}</span>
      </nav>

      <div className="product-detail__layout">
        <section className="product-gallery" aria-label="Product images">
          <div className="product-gallery__thumbs">
            {galleryImages.map((image, index) => (
              <button
                type="button"
                key={`${image}-${index}`}
                className={`product-gallery__thumb ${
                  index === selectedImage ? "is-active" : ""
                }`}
                onClick={() => setSelectedImage(index)}
                aria-label={`View image ${index + 1} of ${galleryImages.length}`}
                aria-current={index === selectedImage ? "true" : undefined}
              >
                {!failedImages[image] ? (
                  <img
                    src={image}
                    alt=""
                    loading="lazy"
                    onError={() => markImageAsFailed(image)}
                  />
                ) : (
                  <span aria-hidden="true">MC</span>
                )}
              </button>
            ))}
          </div>

          <div className="product-gallery__stage">
            {mainImage && !failedImages[mainImage] ? (
              <img
                src={mainImage}
                alt={`${product.name}, view ${selectedImage + 1}`}
                onError={() => markImageAsFailed(mainImage)}
              />
            ) : (
              <div
                className="product-gallery__placeholder"
                role="img"
                aria-label={product.name}
              >
                <span>{siteConfig.brandName}</span>
              </div>
            )}
            {galleryImages.length > 1 && (
              <span className="product-gallery__counter" aria-hidden="true">
                {selectedImage + 1} / {galleryImages.length}
              </span>
            )}
          </div>
        </section>

        <aside className="product-panel">
          <p className="product-panel__brand">{siteConfig.brandName}</p>
          <div className="product-panel__title-row">
            <h1>{product.name}</h1>
            <button
              type="button"
              className={`product-panel__wish ${wishlisted ? "is-active" : ""}`}
              onClick={handleWishlist}
              aria-label={
                wishlisted ? "Remove from wish list" : "Add to wish list"
              }
              aria-pressed={wishlisted}
            >
              <span aria-hidden="true">{wishlisted ? "♥" : "♡"}</span>
            </button>
          </div>
          <p className="product-panel__price">{formatMoney(product.price)}</p>
          <p className="product-panel__tax-note">
            Tax included. Shipping calculated at checkout.
          </p>

          {(product.rating || product.reviews) && (
            <div
              className="product-panel__rating"
              aria-label={`${product.rating} out of 5 stars`}
            >
              <span aria-hidden="true">★★★★★</span>
              <small>
                {product.rating} / 5 · {product.reviews} reviews
              </small>
            </div>
          )}

          <fieldset className="product-option">
            <legend>
              Colour <span>{selectedColor}</span>
            </legend>
            <div className="product-option__values">
              {colorOptions.map((color) => (
                <button
                  type="button"
                  key={color}
                  className={selectedColor === color ? "is-selected" : ""}
                  onClick={() => setSelectedColor(color)}
                  aria-pressed={selectedColor === color}
                >
                  {color}
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset className="product-option">
            <legend>
              Size <span>{selectedSize}</span>
            </legend>
            <div className="product-option__values product-option__values--sizes">
              {sizeOptions.map((size) => (
                <button
                  type="button"
                  key={size}
                  className={selectedSize === size ? "is-selected" : ""}
                  onClick={() => setSelectedSize(size)}
                  aria-pressed={selectedSize === size}
                >
                  {size}
                </button>
              ))}
            </div>
          </fieldset>

          <div className="product-panel__purchase">
            <div className="product-quantity" aria-label="Quantity selector">
              <button
                type="button"
                onClick={() => changeQuantity(quantity - 1)}
                disabled={quantity <= 1}
                aria-label="Decrease quantity"
              >
                −
              </button>
              <output aria-live="polite">{quantity}</output>
              <button
                type="button"
                onClick={() => changeQuantity(quantity + 1)}
                disabled={quantity >= stock}
                aria-label="Increase quantity"
              >
                +
              </button>
            </div>
            <button
              type="button"
              className="product-panel__add"
              onClick={handleAddToCart}
              disabled={soldOut}
            >
              {soldOut ? "Sold out" : "Add to bag"}
            </button>
          </div>

          <div className="product-panel__feedback" aria-live="polite">
            {feedback && (
              <>
                <span>{feedback}</span>
                <Link to="/cartpage" onClick={toTop}>
                  View bag
                </Link>
              </>
            )}
          </div>

          <p className="product-panel__shipping-strip">
            Free EU delivery over {formatMoney(siteConfig.shipping.freeThreshold)} ·{" "}
            {siteConfig.shipping.returnsWindowDays}-day returns
          </p>

          <div className="product-accordions">
            <details open>
              <summary>About this piece</summary>
              <div className="product-accordions__content">
                {(product.description || []).map((paragraph, index) => (
                  <p key={`${product.id}-description-${index}`}>{paragraph}</p>
                ))}
              </div>
            </details>

            <details>
              <summary>Materials &amp; care</summary>
              <div className="product-accordions__content">
                {product.material && <p>{product.material}</p>}
                {attributes.length > 0 && (
                  <dl>
                    {attributes.map(([label, value]) => (
                      <React.Fragment key={label}>
                        <dt>{label}</dt>
                        <dd>{Array.isArray(value) ? value.join(", ") : value}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
                )}
                {features.length > 0 && (
                  <ul>
                    {features.map((feature) => (
                      <li key={feature}>{feature}</li>
                    ))}
                  </ul>
                )}
              </div>
            </details>

            <details>
              <summary>Delivery &amp; returns</summary>
              <div className="product-accordions__content">
                <p>
                  Orders leave our atelier in 1–2 business days. Standard delivery
                  takes {siteConfig.shipping.deliveryWindow}.
                </p>
                <p>
                  Returns are accepted within {siteConfig.shipping.returnsWindowDays}
                  days of delivery.
                </p>
                <Link to="/shipping" onClick={toTop}>
                  Full delivery information
                </Link>
              </div>
            </details>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default ProductDetail;
